import { ExtendedComponentSchema } from '@formio/angular';

interface FormioColumn {
  components?: ExtendedComponentSchema[];
  [key: string]: unknown;
}

interface FormioCell {
  components?: ExtendedComponentSchema[];
  [key: string]: unknown;
}

/**
 * Deep-maps a FormIO component tree, applying `transform` to every component
 * and recursing into the three nesting shapes FormIO uses: `components`
 * (panels, wells, fieldsets, datagrids, editgrids, tabs), `columns[].components`,
 * and `rows[][].components` (tables).
 * Returns new arrays/objects — does NOT mutate the input, even when
 * `transform` returns the component unchanged.
 */
export function mapFormioComponentsDeep(
  components: ExtendedComponentSchema[],
  transform: (component: ExtendedComponentSchema) => ExtendedComponentSchema
): ExtendedComponentSchema[] {
  return components.map(component => {
    const patched: ExtendedComponentSchema = { ...transform(component) };

    if (Array.isArray(component.components)) {
      patched.components = mapFormioComponentsDeep(component.components, transform);
    }

    if (Array.isArray(component.columns)) {
      patched.columns = component.columns.map((column: FormioColumn) => {
        if (Array.isArray(column.components)) {
          return { ...column, components: mapFormioComponentsDeep(column.components, transform) };
        }
        return column;
      });
    }

    if (Array.isArray(component.rows)) {
      patched.rows = component.rows.map((row: unknown) => {
        if (!Array.isArray(row)) return row;
        return row.map((cell: FormioCell) => {
          if (Array.isArray(cell.components)) {
            return { ...cell, components: mapFormioComponentsDeep(cell.components, transform) };
          }
          return cell;
        });
      });
    }

    return patched;
  });
}
